import { Configuration, LogLevel } from '@azure/msal-browser';

const isProduction = process.env.NODE_ENV === 'production';
const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
const storeAuthStateInCookie = process.env.REACT_APP_STORE_AUTH_STATE_IN_COOKIE === 'true';

// Validate required environment variables
const validateConfig = () => {
  if (!process.env.REACT_APP_CLIENT_ID) {
    console.warn('REACT_APP_CLIENT_ID is not set. Azure B2C authentication will not work.');
  }
  if (!process.env.REACT_APP_AUTHORITY) {
    console.warn('REACT_APP_AUTHORITY is not set. Azure B2C authentication will not work.');
  }
  if (!process.env.REACT_APP_KNOWN_AUTHORITY) {
    console.warn('REACT_APP_KNOWN_AUTHORITY is not set. Azure B2C authentication will not work.');
  }
};

// Validate on module load
validateConfig();

// MSAL Configuration for Azure B2C
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID || '', // Azure AD B2C Application (client) ID
    authority: process.env.REACT_APP_AUTHORITY || '', // Authority URL for your B2C tenant and policy
    knownAuthorities: [process.env.REACT_APP_KNOWN_AUTHORITY || ''], // Mark your B2C tenant's domain as trusted
    redirectUri: process.env.REACT_APP_REDIRECT_URI || 'http://localhost:8080/signin', // Redirect URI after login
    postLogoutRedirectUri: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || 'http://localhost:8080/', // Redirect URI after logout
    navigateToLoginRequestUrl: false, // Stay on redirectUri so SignIn can navigate to /client-credentials
  },
  cache: {
    cacheLocation: 'sessionStorage', // Store tokens in session storage
    // Keep false by default; enable only via env for legacy browser compatibility.
    storeAuthStateInCookie,
    // Only meaningful when cookie state is enabled.
    secureCookies: storeAuthStateInCookie && isHttps,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }

        if (isProduction && (level === LogLevel.Info || level === LogLevel.Verbose)) {
          return;
        }

        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
      logLevel: isProduction ? LogLevel.Warning : LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
    windowHashTimeout: 60000,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
    asyncPopups: false,
  },
};

// Scopes for login request
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'], // Basic scopes for user info
  prompt: 'select_account', // Force account selection
};

// Login request for popup
export const loginPopupRequest = {
  scopes: ['openid', 'profile', 'email'],
  prompt: 'select_account',
};

// Login request for redirect
export const loginRedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
  redirectUri: process.env.REACT_APP_REDIRECT_URI || 'http://localhost:8080/signin',
};

// Scopes for API calls (if you need to call protected APIs)
export const apiRequest = {
  scopes: process.env.REACT_APP_API_SCOPES ? 
    process.env.REACT_APP_API_SCOPES.split(',') : 
    [], // API scopes from environment
};


export const tokenRequest = {
  scopes: ["openid", "profile", "email"],
};


// Graph API configuration (if needed)
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
};

// Protected resource map for token acquisition
export const protectedResources = {
  api: {
    endpoint: process.env.REACT_APP_API_ENDPOINT || '',
    scopes: apiRequest.scopes,
  },
};